import type { InvitationPreview } from "./types";

export class PreviewRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PreviewRequestError";
  }
}

// Calls the existing, already-reviewed preview_invitation_public(uuid)
// RPC (supabase/migrations/202609110001_..., part 7) directly over
// PostgREST with only the anon/publishable key — no supabase-js
// dependency needed for a single read call, keeping this app's bundle
// free of any dependency that could carry service-role-capable code
// paths. The invitation id (a server-generated, non-secret identifier)
// is the ONLY thing this function ever sends; it never receives, and
// therefore can never leak, anything read from window.location.hash.
export async function fetchInvitationPreview(
  supabaseUrl: string,
  supabaseAnonKey: string,
  invitationId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<InvitationPreview | null> {
  const endpoint = `${supabaseUrl.replace(/\/+$/, "")}/rest/v1/rpc/preview_invitation_public`;

  let response: Response;
  try {
    response = await fetchImpl(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({ p_invitation_id: invitationId }),
      credentials: "omit",
      referrerPolicy: "no-referrer",
    });
  } catch (err) {
    throw new PreviewRequestError(err instanceof Error ? err.message : String(err));
  }

  if (!response.ok) {
    throw new PreviewRequestError(`preview request failed with status ${response.status}`);
  }

  let data: unknown;
  try {
    data = await response.json();
  } catch {
    throw new PreviewRequestError("preview response was not valid JSON");
  }

  // The RPC returns SQL NULL (-> JSON null here) when no invitation with
  // that id exists — treated as "unknown invitation", not an error.
  if (data === null) return null;
  return data as InvitationPreview;
}
